// Apple Music implementation of MusicProvider, backed by MusicKit-JS v3. All MusicKit
// specifics live here — components/store never import MusicKit. The server-minted developer
// token is fetched via /apple/developer-token (the .p8 never reaches the browser); the Music
// User Token from authorize() stays inside MusicKit and is never read or sent to KaziPeople.
import { getAppleMusicDeveloperToken, connectAppleMusic, disconnectAppleMusic } from '@/services/performanceService';
import type {
  MusicProvider, MusicAuthorizationState, MusicSearchResults, MusicSearchType, MusicPageRequest,
  MusicItem, MusicItemKind, MusicAlbumDetails, MusicArtistDetails, MusicPlaylistDetails,
  MusicPlayableItem, NowPlayingSnapshot, MusicArtworkRef, PlaybackStatus,
} from './types';

const SRC = 'https://js-cdn.music.apple.com/musickit/v3/musickit.js';
type MK = any;

let scriptPromise: Promise<MK> | null = null;
function loadMusicKit(): Promise<MK> {
  const w = window as unknown as { MusicKit?: MK };
  if (w.MusicKit) return Promise.resolve(w.MusicKit);
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise<MK>((resolve, reject) => {
    document.addEventListener('musickitloaded', () => (w.MusicKit ? resolve(w.MusicKit) : reject(new Error('MusicKit init failed'))), { once: true });
    if (!document.getElementById('apple-musickit-js')) {
      const s = document.createElement('script');
      s.id = 'apple-musickit-js'; s.src = SRC; s.async = true; s.crossOrigin = 'anonymous';
      s.onerror = () => reject(new Error('Could not load Apple MusicKit'));
      document.head.appendChild(s);
    }
  }).catch((e) => { scriptPromise = null; throw e; });
  return scriptPromise;
}

async function instance(): Promise<MK> {
  const MusicKit = await loadMusicKit();
  if (!MusicKit.getInstance()) {
    const { token } = await getAppleMusicDeveloperToken();
    await MusicKit.configure({ developerToken: token, app: { name: 'KaziPeople', build: '1.0.0' } });
  }
  return MusicKit.getInstance();
}

function playbackStatus(m: MK): PlaybackStatus {
  // MusicKit PlaybackStates: 0 none,1 loading,2 playing,3 paused,4 stopped,5 ended,6 seeking,8 waiting,9 stalled
  switch (m?.playbackState) {
    case 2: return 'playing';
    case 3: case 4: case 5: return 'paused';
    case 1: case 6: return 'loading';
    case 8: case 9: return 'buffering';
    default: return m?.nowPlayingItem ? 'paused' : 'idle';
  }
}

function toItem(kind: MusicItemKind, raw: any): MusicItem {
  const a = raw?.attributes ?? {};
  return {
    kind, id: raw?.id, provider: 'apple_music',
    title: a.name ?? a.title ?? '',
    subtitle: a.artistName ?? a.curatorName ?? a.name,
    artist: a.artistName, album: a.albumName,
    artwork: a.artwork ? { url: a.artwork.url, bgColor: a.artwork.bgColor } : undefined,
    durationMs: a.durationInMillis,
    isExplicit: a.contentRating === 'explicit',
    isrc: a.isrc,
    canonicalUrl: a.url,
    playable: a.playParams != null,
  };
}
function nowPlayingToItem(np: any): MusicItem | null {
  if (!np) return null;
  const a = np.attributes ?? np;
  return {
    kind: 'song', id: np.id ?? a.playParams?.id ?? '', provider: 'apple_music',
    title: a.title ?? a.name ?? '', subtitle: a.artistName, artist: a.artistName, album: a.albumName,
    artwork: a.artwork ? { url: a.artwork.url, bgColor: a.artwork.bgColor } : undefined,
    durationMs: a.durationInMillis, isExplicit: a.contentRating === 'explicit', canonicalUrl: a.url,
  };
}

export class AppleMusicProvider implements MusicProvider {
  readonly id = 'apple_music' as const;
  private listeners = new Set<() => void>();
  private wired = false;

  private async wireEvents(m: MK) {
    if (this.wired) return;
    this.wired = true;
    const emit = () => this.listeners.forEach((l) => l());
    ['playbackStateDidChange', 'nowPlayingItemDidChange', 'playbackTimeDidChange',
     'playbackDurationDidChange', 'queueItemsDidChange', 'shuffleModeDidChange', 'repeatModeDidChange']
      .forEach((ev) => { try { m.addEventListener(ev, emit); } catch { /* older API */ } });
  }

  async getAuthorizationState(): Promise<MusicAuthorizationState> {
    let m: MK;
    try { m = await instance(); } catch { return 'missing_configuration'; }
    if (!m) return 'provider_unavailable';
    if (!m.isAuthorized) return 'not_connected';
    return this.probeEligibility(m);
  }

  private async probeEligibility(m: MK): Promise<MusicAuthorizationState> {
    // A user token is present; verify catalog playback eligibility (subscription). We ask a
    // cheap authorized endpoint; a 403/subscription error → connected but no subscription.
    try {
      await m.api.music(`/v1/me/storefront`);
      return 'connected_eligible';
    } catch (e: any) {
      const code = e?.status ?? e?.errorCode;
      if (code === 403) return 'connected_no_subscription';
      return 'connected_eligible'; // transient errors shouldn't demote a valid auth
    }
  }

  async authorize(): Promise<MusicAuthorizationState> {
    const m = await instance();
    await this.wireEvents(m);
    await m.authorize(); // Apple's own sheet
    await connectAppleMusic();
    return this.probeEligibility(m);
  }

  async disconnect(): Promise<void> {
    await disconnectAppleMusic();
    try { const m = await instance(); if (m?.isAuthorized) await m.unauthorize(); } catch { /* best effort */ }
  }

  private async storefront(m: MK): Promise<string> {
    return m.storefrontId || 'us';
  }

  async searchCatalog(query: string, types: MusicSearchType[], page?: MusicPageRequest): Promise<MusicSearchResults> {
    const m = await instance();
    const sf = await this.storefront(m);
    const res = await m.api.music(`/v1/catalog/${sf}/search`, {
      term: query, types: types.join(','), limit: page?.limit ?? 24, offset: page?.offset ?? 0,
    });
    const r = res?.data?.results ?? {};
    const songs = (r.songs?.data ?? []).map((x: any) => toItem('song', x));
    const albums = (r.albums?.data ?? []).map((x: any) => toItem('album', x));
    const artists = (r.artists?.data ?? []).map((x: any) => toItem('artist', x));
    const playlists = (r.playlists?.data ?? []).map((x: any) => toItem('playlist', x));
    const top = r.top?.data?.[0];
    return {
      topResult: top ? toItem((top.type || 'song').replace(/s$/, '') as MusicItemKind, top) : songs[0] ?? albums[0],
      songs, albums, artists, playlists,
      nextOffset: (page?.offset ?? 0) + (songs.length || albums.length || 0) || undefined,
    };
  }

  async getAlbum(id: string): Promise<MusicAlbumDetails> {
    const m = await instance(); const sf = await this.storefront(m);
    const res = await m.api.music(`/v1/catalog/${sf}/albums/${id}`, { include: 'tracks' });
    const raw = res?.data?.data?.[0]; const base = toItem('album', raw); const a = raw?.attributes ?? {};
    return { ...base, releaseDate: a.releaseDate, genre: a.genreNames?.[0], trackCount: a.trackCount,
      tracks: (raw?.relationships?.tracks?.data ?? []).map((t: any) => toItem('song', t)) };
  }
  async getArtist(id: string): Promise<MusicArtistDetails> {
    const m = await instance(); const sf = await this.storefront(m);
    const res = await m.api.music(`/v1/catalog/${sf}/artists/${id}`, { views: 'top-songs,full-albums', include: 'albums' });
    const raw = res?.data?.data?.[0]; const base = toItem('artist', raw);
    const views = raw?.views ?? {};
    return { ...base, bio: raw?.attributes?.artistBio,
      topSongs: (views['top-songs']?.data ?? []).map((t: any) => toItem('song', t)),
      albums: (raw?.relationships?.albums?.data ?? views['full-albums']?.data ?? []).map((t: any) => toItem('album', t)) };
  }
  async getPlaylist(id: string): Promise<MusicPlaylistDetails> {
    const m = await instance(); const sf = await this.storefront(m);
    const res = await m.api.music(`/v1/catalog/${sf}/playlists/${id}`, { include: 'tracks' });
    const raw = res?.data?.data?.[0]; const base = toItem('playlist', raw); const a = raw?.attributes ?? {};
    return { ...base, description: a.description?.standard, curator: a.curatorName, origin: 'apple_editorial',
      trackCount: (raw?.relationships?.tracks?.data ?? []).length,
      tracks: (raw?.relationships?.tracks?.data ?? []).map((t: any) => toItem('song', t)) };
  }
  async getRecentlyPlayed(): Promise<MusicItem[]> {
    const m = await instance();
    try { const res = await m.api.music('/v1/me/recent/played/tracks', { limit: 20 });
      return (res?.data?.data ?? []).map((t: any) => toItem('song', t)); } catch { return []; }
  }

  private async queueAndPlay(m: MK, opts: any) { await m.setQueue({ ...opts, startPlaying: true }); }
  async play(item: MusicPlayableItem): Promise<void> {
    const m = await instance(); await this.wireEvents(m);
    const key = item.kind === 'song' ? 'song' : item.kind; // song|album|playlist
    try { await this.queueAndPlay(m, { [key]: item.id }); }
    catch (e: any) { if ((e?.status ?? e?.errorCode) === 403) throw new Error('no_subscription'); throw e; }
  }
  async pause() { (await instance()).pause?.(); }
  async resume() { (await instance()).play?.(); }
  async seek(seconds: number) { (await instance()).seekToTime?.(seconds); }
  async skipNext() { (await instance()).skipToNextItem?.(); }
  async skipPrevious() { (await instance()).skipToPreviousItem?.(); }
  // startPlaying=false is how a reloaded page gets its queue back without fighting the
  // browser's autoplay rules (see player/session.ts).
  async setQueue(items: MusicPlayableItem[], startIndex = 0, startPlaying = true) {
    const m = await instance(); await this.wireEvents(m);
    const songs = items.filter((i) => i.kind === 'song').map((i) => i.id);
    if (!songs.length) return;
    try { await m.setQueue({ songs, startWith: Math.min(Math.max(0, startIndex), songs.length - 1), startPlaying }); }
    catch (e: any) { if ((e?.status ?? e?.errorCode) === 403) throw new Error('no_subscription'); throw e; }
  }
  async setShuffle(on: boolean) { const m = await instance(); m.shuffleMode = on ? 1 : 0; }
  async setRepeat(mode: 'off' | 'one' | 'all') { const m = await instance(); m.repeatMode = mode === 'one' ? 1 : mode === 'all' ? 2 : 0; }
  async setVolume(level: number) { const m = await instance(); m.volume = Math.max(0, Math.min(1, level)); }

  snapshot(): NowPlayingSnapshot {
    const w = window as unknown as { MusicKit?: MK };
    const m = w.MusicKit?.getInstance?.();
    if (!m) return { item: null, status: 'idle', positionMs: 0, durationMs: 0, shuffle: false, repeat: 'off' };
    return {
      item: nowPlayingToItem(m.nowPlayingItem),
      status: playbackStatus(m),
      positionMs: Math.round((m.currentPlaybackTime ?? 0) * 1000),
      durationMs: Math.round((m.currentPlaybackDuration ?? 0) * 1000),
      shuffle: m.shuffleMode === 1,
      repeat: m.repeatMode === 1 ? 'one' : m.repeatMode === 2 ? 'all' : 'off',
    };
  }
  subscribe(onChange: () => void): () => void {
    this.listeners.add(onChange);
    // also poll at 1s so the progress bar advances even without time events
    const t = window.setInterval(onChange, 1000);
    return () => { this.listeners.delete(onChange); window.clearInterval(t); };
  }
  // Current MusicKit queue as provider-neutral items (for the Queue panel). Read-only.
  queueItems(): { current: number; items: MusicItem[] } {
    const w = window as unknown as { MusicKit?: MK };
    const m = w.MusicKit?.getInstance?.();
    const q = m?.queue;
    const items = (q?.items ?? []).map((x: any) => nowPlayingToItem(x)).filter(Boolean) as MusicItem[];
    return { current: q?.position ?? 0, items };
  }
  artworkUrl(art: MusicArtworkRef | undefined, px: number): string | undefined {
    if (!art?.url) return undefined;
    return art.url.replace('{w}', String(px)).replace('{h}', String(px)).replace('{f}', 'jpg');
  }
}

let singleton: AppleMusicProvider | null = null;
export function appleMusicProvider(): AppleMusicProvider {
  if (!singleton) singleton = new AppleMusicProvider();
  return singleton;
}
